import {
  keepPreviousData,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { showCustomToast } from "@/shared/components/generic/snackbar/showCustomToast";
import IconRoundedExclamationMark from "@/shared/components/generic/icons/IconRoundedExclamationMark";

/**
 * please DO NOT use this component outside of the App file
 */
export const useConfiguredQueryClient = (): QueryClient => {
  const { t } = useTranslation();

  const createErrorHandler = (error: Error): boolean => {
    let errorMessage = error.message;
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        errorMessage = t("common:error.forbidden");
      } else if (error.response?.status === 409) {
        errorMessage = t("common:error.conflict");
      } else {
        errorMessage = t("common:error.unexpected");
      }
    }
    showCustomToast({
      message: errorMessage,
      style: "ERROR",
      icon: IconRoundedExclamationMark,
      showCloseIcon: true,
    });
    return false;
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 1000 * 5,
            placeholderData: keepPreviousData,
          },
        },
        queryCache: new QueryCache({
          onError: createErrorHandler,
        }),
        mutationCache: new MutationCache({
          onError: createErrorHandler,
        }),
      }),
  );

  return queryClient;
};
